import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';

const dialogPaperSx = {
  borderRadius: 3,
  backgroundColor: 'surface.content',
  backgroundImage: 'none',
  border: '1px solid',
  borderColor: 'hairline.main'
};

// Spinner dorado (acento de marca) reutilizado en processing / timeout / extended.
function GoldSpinner({ size = 88 }) {
  return <CircularProgress size={size} thickness={3} sx={{ color: 'gold.main' }} />;
}

GoldSpinner.propTypes = { size: PropTypes.number };
/**
 * Modal del estado del procesamiento de pago (estilo OTRIX monocromo: superficie off-black,
 * acento dorado, CTA blanco). El check verde de éxito y la X roja de error se conservan
 * (colores semánticos).
 * Estados: processing | success | timeout | extended-timeout | failed
 */
function PaymentProcessingModal({ open, status, timeRemaining = 300, onClose, onRetryPayment }) {
  const { t } = useTranslation(['payments']);

  const isProcessing = status === 'processing';
  const isSuccess = status === 'success';
  const isTimeout = status === 'timeout';
  const isExtendedTimeout = status === 'extended-timeout';
  const isFailed = status === 'failed';

  // Formatear tiempo restante en formato MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="text.primary">
          {isProcessing && t('processing.title', { ns: 'payments' })}
          {isSuccess && t('success.title', { ns: 'payments' })}
          {isTimeout && t('timeout.title', { ns: 'payments' })}
          {isExtendedTimeout && t('extendedTimeout.title', { ns: 'payments' })}
          {isFailed && t('failed.title', { ns: 'payments' })}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 4 }}>
        <Stack spacing={3} alignItems="center">
          {/* Processing */}
          {isProcessing && (
            <>
              <Box sx={{ my: 3 }}>
                <GoldSpinner />
              </Box>
              <Typography variant="body1" color="text.primary" textAlign="center">
                {t('processing.message', { ns: 'payments' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} textAlign="center">
                {t('processing.submessage', { ns: 'payments' })}
              </Typography>

              <Box sx={{ mt: 3, mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} textAlign="center">
                  {t('processing.timeRemaining', { ns: 'payments', defaultValue: 'Tiempo restante' })}
                </Typography>
                <Typography
                  variant="h4"
                  color={timeRemaining < 60 ? 'error.main' : 'text.primary'}
                  fontWeight="bold"
                  sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                  {formatTime(timeRemaining)}
                </Typography>
              </Box>

              <Button onClick={onClose} fullWidth sx={{ ...premiumButtonSx, mt: 3 }}>
                {t('processing.cancel', { ns: 'payments' })}
              </Button>
            </>
          )}

          {/* Success — check verde semántico */}
          {isSuccess && (
            <>
              <Box
                sx={{
                  my: 3,
                  animation: 'successPulse 0.6s ease-in-out',
                  '@keyframes successPulse': {
                    '0%': { transform: 'scale(0)', opacity: 0 },
                    '50%': { transform: 'scale(1.1)' },
                    '100%': { transform: 'scale(1)', opacity: 1 }
                  }
                }}>
                <CheckCircleIcon sx={{ fontSize: 100, color: 'success.main' }} />
              </Box>
              <Typography variant="body1" color="text.primary" textAlign="center" fontWeight="medium">
                {t('success.message', { ns: 'payments' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} textAlign="center">
                {t('success.submessage', { ns: 'payments' })}
              </Typography>
              <Button onClick={onClose} fullWidth sx={{ ...premiumButtonSx, mt: 2 }}>
                {t('success.button', { ns: 'payments' })}
              </Button>
            </>
          )}

          {/* Timeout — fast polling automático */}
          {isTimeout && (
            <>
              <Box sx={{ my: 3 }}>
                <GoldSpinner />
              </Box>
              <Typography variant="body1" color="text.primary" textAlign="center">
                {t('timeout.message', { ns: 'payments' })}
              </Typography>
            </>
          )}

          {/* Extended timeout */}
          {isExtendedTimeout && (
            <>
              <Box sx={{ my: 3 }}>
                <GoldSpinner />
              </Box>
              <Typography variant="body1" color="text.primary" textAlign="center">
                {t('extendedTimeout.message', { ns: 'payments' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} textAlign="center">
                {t('extendedTimeout.submessage', { ns: 'payments' })}
              </Typography>
              <Button onClick={onRetryPayment} fullWidth sx={{ ...premiumButtonSx, mt: 3 }}>
                {t('extendedTimeout.retry', { ns: 'payments' })}
              </Button>
            </>
          )}

          {/* Failed — X roja semántica */}
          {isFailed && (
            <>
              <Box
                sx={{
                  my: 3,
                  animation: 'failPulse 0.6s ease-in-out',
                  '@keyframes failPulse': {
                    '0%': { transform: 'scale(0)', opacity: 0 },
                    '50%': { transform: 'scale(1.1)' },
                    '100%': { transform: 'scale(1)', opacity: 1 }
                  }
                }}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    flex: 'none',
                    border: '5px solid #f44336',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                  <Box sx={{ width: 60, height: 5, background: '#f44336', transform: 'rotate(45deg)', position: 'absolute' }} />
                  <Box sx={{ width: 60, height: 5, background: '#f44336', transform: 'rotate(-45deg)', position: 'absolute' }} />
                </Box>
              </Box>
              <Typography variant="body1" color="text.primary" textAlign="center" fontWeight="medium">
                {t('failed.message', { ns: 'payments' })}
              </Typography>
              <Button onClick={onClose} fullWidth sx={{ ...premiumButtonSx, mt: 2 }}>
                {t('failed.button', { ns: 'payments' })}
              </Button>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

PaymentProcessingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  status: PropTypes.oneOf(['processing', 'success', 'timeout', 'extended-timeout', 'failed']).isRequired,
  timeRemaining: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onRetryPayment: PropTypes.func.isRequired
};

export default PaymentProcessingModal;
